// src/context/AuthContext.tsx
import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { getInitialToken, loginUser } from '../services/api';
import { TOKEN_KEY } from '../config/constants';

import { User  } from "../types/user";

interface AuthContextType {
    user: User | null;
    isInitializing: boolean;
    initialToken: string | null;
    error: string | null;
    login: (username: string, password: string) => Promise<boolean>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(() => {
        const storedUser = localStorage.getItem('user');
        return storedUser ? JSON.parse(storedUser) : null;
    });
    const [isInitializing, setIsInitializing] = useState(true);
    const [initialToken, setInitialToken] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Usar una ref para rastrear si ya se hizo la llamada
    const initializationAttempted = useRef(false);

    useEffect(() => {
        const getToken = async () => {
            // Verificar si ya se intentó inicializar
            if (initializationAttempted.current) return;
            initializationAttempted.current = true;

            try {
                console.log('Iniciando sesión en el servidor...'); // Para debug
                const token = await getInitialToken();
                setInitialToken(token);
                localStorage.setItem(TOKEN_KEY, token);
            } catch (error) {
                console.error('Error fetching initial token:', error);
                setError('Error al obtener el token inicial');
            } finally {
                setIsInitializing(false);
            }
        };

        getToken();
    }, []); // dependencias vacías

    const login = useCallback(async (username: string, password: string): Promise<boolean> => {
        if (!initialToken) {
            setError('No se pudo obtener el token inicial');
            return false;
        }

        try {
            const data = await loginUser(username, password, initialToken);
            if (data.response === "1") {
                const userData: User = data.data;
                // const userData: User = {
                //     id: data.data.id,
                //     username: data.data.username,
                //     token: data.data.token
                // };
                setUser(userData);
                localStorage.setItem('user', JSON.stringify(userData));
                setError(null);
                return true;
            } else {
                setError(data.data.msg || 'Error en el inicio de sesión');
                return false;
            }
        } catch (error) {
            if (error instanceof Error) {
                setError(error.message);
            } else {
                setError('Error desconocido en el inicio de sesión');
            }
            return false;
        }
    }, [initialToken]);

    const logout = useCallback(() => {
        setUser(null);
        localStorage.removeItem('user');
    }, []);

    const value = {
        user,
        isInitializing,
        initialToken,
        error,
        login,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe ser usado dentro de un AuthProvider');
    }
    return context;
};